import React, { useState, useEffect } from 'react';
import {
    Box, Button, TextField, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, Typography, IconButton, Dialog,
    DialogTitle, DialogContent, DialogActions, Grid, Stack, Autocomplete, CircularProgress
} from '@mui/material';
import { Add, Edit, Delete, Print, Search } from '@mui/icons-material';
import { collection, addDoc, getDocs, getDoc, query, orderBy, deleteDoc, doc, updateDoc, where, Timestamp } from "firebase/firestore";
import { db, auth } from "../../firebase"; 
import Swal from 'sweetalert2';

const PRIMARY = "#0d9488";

function Hosteloutpass() {
    const [outPasses, setOutPasses] = useState([]);
    const [students, setStudents] = useState([]); // Boarding students list
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const [schoolId, setSchoolId] = useState(null);
    const [printData, setPrintData] = useState(null);

    const [formData, setFormData] = useState({
        studentId: '',
        studentName: '',
        gradeClass: '',
        parentsName: '',
        parentsContact: '',
        parentsAdhar: '',
        day: '',
        date: new Date().toISOString().split('T')[0],
        outTime: '',
        roomNo: '',
        emergencyNo: '',
        reason: '',
        fromDate: new Date().toISOString().split('T')[0],
        toDate: '',
        totalDays: '',
        expectedReturnTime: '',
        returnDate: ''
    });

    useEffect(() => {
        const init = async () => {
            if (!auth.currentUser) return;
            const userSnap = await getDoc(doc(db, "users", auth.currentUser.uid));
            if (userSnap.exists()) {
                const sId = userSnap.data().schoolId;
                setSchoolId(sId);
                fetchBoardingStudents(sId);
                fetchOutPasses(sId);
            }
        };
        init();
    }, []);

  const fetchBoardingStudents = async (sId) => {
    try {
        const [admSnap, studentSnap, courseSnap, roomSnap] = await Promise.all([
            getDocs(query(collection(db, "admission"), where("schoolId", "==", sId), where("studenttype", "==", "Boarding"), where('status', '==', 'Active'))),
            getDocs(query(collection(db, "students"), where("schoolId", "==", sId))),
            getDocs(query(collection(db, "courses"), where("schoolId", "==", sId))),
            getDocs(query(collection(db, "roomallocations"), where("schoolId", "==", sId))) // Fetch Rooms
        ]);

        const studentMap = {};
        studentSnap.docs.forEach(d => { studentMap[d.id] = d.data(); });

        const courseMap = {};
        courseSnap.docs.forEach(d => { courseMap[d.id] = d.data(); });

        // Map Student ID to Room Number
        const roomMap = {};
        roomSnap.docs.forEach(d => {
            const data = d.data();
            roomMap[data.studentId] = data.roomId;
        });

        const list = admSnap.docs.map((adm) => {
            const admData = adm.data();
            const sData = studentMap[admData.studentId];
            return {
                id: admData.studentId,
                name: `${sData?.firstname || ''} ${sData?.fathername || ''} ${sData?.surname || ''}`.toUpperCase(),
                className: courseMap[admData.class]?.name || "N/A",
                parentName: sData?.fathername || '',
                parentContact: sData?.fathermobile || '',
                adhar: sData?.adharno || '',
                roomNo: roomMap[admData.studentId] || 'Not Assigned', // Add Room No here
                label: `${sData?.firstname || ''} ${sData?.fathername || ''} ${sData?.surname || ''} - ${courseMap[admData.class]?.name || ''}`.toUpperCase()
            };
        });
        setStudents(list);
    } catch (err) { console.error("Error fetching students/rooms:", err); }
};

    const fetchOutPasses = async (sId) => {
        const q = query(collection(db, "hostel_out_passes"), where("schoolId", "==", sId), orderBy("timestamp", "desc"));
        const snap = await getDocs(q);
        setOutPasses(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    };

   const handleStudentSelect = (val) => {
    if (val) {
        setFormData({
            ...formData,
            studentId: val.id,
            studentName: val.name,
            gradeClass: val.className,
            parentsName: val.parentName,
            parentsContact: val.parentContact,
            parentsAdhar: val.adhar,
            roomNo: val.roomNo // Auto-fill room number from allocation
        });
    }
};

    const handleSave = async () => {
        if (!formData.studentId) return Swal.fire("Error", "Please select a student", "error");
        setLoading(true);
        try {
            const data = { ...formData, schoolId, timestamp: Timestamp.now() };
            if (editId) {
                await updateDoc(doc(db, "hostel_out_passes", editId), data);
            } else {
                const srNo = outPasses.length > 0 ? (outPasses[0].srNo + 1) : 1;
                await addDoc(collection(db, "hostel_out_passes"), { ...data, srNo });
            }
            setOpen(false);
            fetchOutPasses(schoolId);
            Swal.fire("Success", "Out Pass Saved", "success");
        } catch (e) { Swal.fire("Error", e.message, "error"); }
        setLoading(false);
    };

    const triggerPrint = (item) => {
        setPrintData(item);
        setTimeout(() => { window.print(); }, 700);
    };

    return (
        <Box p={2} sx={{ bgcolor: "#f8fafc", minHeight: "100vh" }}>
            <style>
                {`
                @media print {
                    body * { visibility: hidden; }
                    #print-area, #print-area * { visibility: visible; }
                    #print-area { position: absolute; left: 0; top: 0; width: 100%; display: block !important; }
                }
                .pass-card { width: 200mm; border: 2px solid #000; padding: 20px; font-family: serif; background: #fff; }
                .line { border-bottom: 1px dotted #000; display: inline-block; padding: 0 5px; font-weight: bold; }
                `}
            </style>

            <Stack direction="row" justifyContent="space-between" mb={3}>
                <Typography variant="h6" fontWeight="bold">Hostel Student Out-Pass</Typography>
                <Button variant="contained" sx={{ bgcolor: PRIMARY }} startIcon={<Add />} onClick={() => { setEditId(null); setOpen(true); }}>New Out Pass</Button>
            </Stack>

            <Paper elevation={0} sx={{ border: '1px solid #e2e8f0' }}>
                <Table size="small">
                    <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                        <TableRow>
                            <TableCell>Sr.</TableCell>
                            <TableCell>Student Name</TableCell>
                            <TableCell>Class</TableCell>
                            <TableCell>Reason</TableCell>
                            <TableCell align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {outPasses.map((item, index) => (
                            <TableRow key={item.id}>
                                <TableCell>{item.srNo}</TableCell>
                                <TableCell><b>{item.studentName}</b></TableCell>
                                <TableCell>{item.gradeClass}</TableCell>
                                <TableCell>{item.reason}</TableCell>
                                <TableCell align="center">
                                    <IconButton color="primary" onClick={() => triggerPrint(item)}><Print /></IconButton>
                                    <IconButton onClick={() => { setEditId(item.id); setFormData(item); setOpen(true); }}><Edit /></IconButton>
                                    <IconButton color="error" onClick={async () => { await deleteDoc(doc(db, "hostel_out_passes", item.id)); fetchOutPasses(schoolId); }}><Delete /></IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Paper>

            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>{editId ? "Edit Out Pass" : "New Hostel Out Pass"}</DialogTitle>
                <DialogContent dividers>
                    <Grid className="row" spacing={2} sx={{ mt: 1 }}>
                        <Grid className="col-sm-4 mb-3" item xs={12}>
                            <Autocomplete
                                options={students}
                                getOptionLabel={(o) => o.label}
                                onChange={(e, v) => handleStudentSelect(v)}
                                renderInput={(params) => <TextField {...params} label="Select Boarding Student" size="small" />}
                            />
                        </Grid>
                        <Grid className="col-sm-4 mb-3" item xs={4}><TextField fullWidth label="Date" type="date" size="small" InputLabelProps={{ shrink: true }} value={formData.date} onChange={e => setFormData({ ...formData, date: e.target.value })} /></Grid>
                        <Grid className="col-sm-4 mb-3" item xs={4}><TextField fullWidth type='time' label="Out Time" size="small" value={formData.outTime} onChange={e => setFormData({ ...formData, outTime: e.target.value })} /></Grid>
                        <Grid className="col-sm-4 mb-3" item xs={4}><TextField fullWidth label="Room No" size="small" value={formData.roomNo} onChange={e => setFormData({ ...formData, roomNo: e.target.value })} /></Grid>
                        
                        <Grid className="col-sm-4 mb-3" item xs={6}><TextField disabled fullWidth label="Student Name" size="small" value={formData.studentName} /></Grid>
                        <Grid className="col-sm-4 mb-3" item xs={6}><TextField disabled fullWidth label="Class" size="small" value={formData.gradeClass} /></Grid>
                        
                        <Grid className="col-sm-4 mb-3" item xs={6}><TextField fullWidth label="Parents Name" size="small" value={formData.parentsName} onChange={e => setFormData({ ...formData, parentsName: e.target.value })} /></Grid>
                        <Grid className="col-sm-4 mb-3" item xs={6}><TextField fullWidth label="Parents Contact" size="small" value={formData.parentsContact} onChange={e => setFormData({ ...formData, parentsContact: e.target.value })} /></Grid>

                        <Grid className="col-sm-4 mb-3" item xs={12}><TextField fullWidth label="Reason for Leave" size="small" value={formData.reason} onChange={e => setFormData({ ...formData, reason: e.target.value })} /></Grid>
                        
                        <Grid className="col-sm-4 mb-3" item xs={4}><TextField fullWidth label="From Date" type="date" size="small" InputLabelProps={{ shrink: true }} value={formData.fromDate} onChange={e => setFormData({ ...formData, fromDate: e.target.value })} /></Grid>
                        <Grid className="col-sm-4 mb-3" item xs={4}><TextField fullWidth label="To Date" type="date" size="small" InputLabelProps={{ shrink: true }} value={formData.toDate} onChange={e => setFormData({ ...formData, toDate: e.target.value })} /></Grid>
                        <Grid className="col-sm-4 mb-3" item xs={4}><TextField fullWidth label="Total Days" size="small" value={formData.totalDays} onChange={e => setFormData({ ...formData, totalDays: e.target.value })} /></Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Cancel</Button>
                    <Button variant="contained" sx={{ bgcolor: PRIMARY }} onClick={handleSave} disabled={loading}>
                        {loading ? <CircularProgress size={24} /> : "Save & Print Ready"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Print View */}
            <div id="print-area" style={{ display: 'none' }}>
                {printData && (
                    <div className="pass-card" style={{
                        width: '310mm',
                        minHeight: '148mm', // Half A4 size (A5 landscape) looks great for passes
                        padding: '20px',
                        backgroundColor: 'white',
                        margin: '0 auto'
                    }}>
                         <Box textAlign="center" borderBottom="1px solid #000" pb={1} mb={2}>
                            <Typography variant="h5" fontWeight="900">MOHAN MALI INTERNATIONAL SCHOOL</Typography>
                            <Typography variant="caption">HOSTEL STUDENT OUT PASS</Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between">
                             <Typography>No: <span className="line">{printData.srNo}</span></Typography>
                             <Typography>Date: <span className="line">{printData.date}</span></Typography>
                        </Box>
                        <Box sx={{ mt: 2, lineHeight: 2.5 }}>
                            <Typography>Student Name: <span className="line" style={{ width: '70%' }}>{printData.studentName}</span></Typography>
                            <Typography>Class: <span className="line" style={{ width: '30%' }}>{printData.gradeClass}</span> Room: <span className="line" style={{ width: '20%' }}>{printData.roomNo}</span></Typography>
                            <Typography>Reason: <span className="line" style={{ width: '80%' }}>{printData.reason}</span></Typography>
                            <Typography>Duration: <span className="line">{printData.fromDate}</span> to <span className="line">{printData.toDate}</span></Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" mt={5}>
                             <Typography fontWeight="bold">Parent Sign</Typography>
                             <Typography fontWeight="bold">Warden Sign</Typography>
                             <Typography fontWeight="bold">Principal Sign</Typography>
                        </Box>
                    </div>
                )}
            </div>
        </Box>
    );
}

export default Hosteloutpass;